import React, { useState } from 'react'
import { Calendar, FileText, Clock } from 'lucide-react'
import LeaveTypes from '../../../components/hr/leave/LeaveTypes'
import LeaveApplications from '../../../components/hr/leave/LeaveApplications'
import PendingLeaves from '../../../components/hr/leave/PendingLeaves'
import { usePermissions } from '../../../hooks/usePermissions'
import { PERMISSIONS } from '../../../config/roles'
import AuthService from '../../../services/authService'

const LeaveManagement = () => {
  const [activeTab, setActiveTab] = useState('leave-types')
  
  // Get current user and permissions
  const currentUser = AuthService.getCurrentUser()
  const permissions = usePermissions(currentUser)

  const tabs = [
    {
      id: 'leave-types',
      label: 'Leave Types',
      icon: Calendar,
      component: LeaveTypes,
      requiredPermissions: [PERMISSIONS.HR.VIEW_LEAVE_TYPES]
    },
    {
      id: 'leave-applications',
      label: 'Leave',
      icon: FileText,
      component: LeaveApplications,
      requiredPermissions: [PERMISSIONS.HR.VIEW_LEAVES]
    },
    {
      id: 'pending-leaves',
      label: 'Pending Leaves',
      icon: Clock,
      component: PendingLeaves,
      requiredPermissions: [PERMISSIONS.HR.VIEW_PENDING_LEAVES]
    }
  ]

  // Filter tabs based on user permissions
  const availableTabs = tabs.filter(tab => 
    permissions.hasAnyPermission(tab.requiredPermissions)
  )

  // Set default active tab to first available tab
  React.useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(tab => tab.id === activeTab)) {
      setActiveTab(availableTabs[0].id)
    }
  }, [availableTabs, activeTab])

  // Get active tab component
  const ActiveTabComponent = availableTabs.find(tab => tab.id === activeTab)?.component || (() => <div>No access</div>)

  if (availableTabs.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Calendar className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Access Restricted
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>You don't have permission to access Leave Management features.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {availableTabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ActiveTabComponent />
      </div>
    </div>
  )
}

export default LeaveManagement
